import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, ExternalLink, BookOpen, FileText, Video, HelpCircle, Shield, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";

const resources = [
  {
    category: "Getting Started",
    items: [
      { title: "Quick Start Guide", desc: "Learn the basics of Odesie in under 10 minutes", icon: Zap, type: "guide" },
      { title: "Platform Overview", desc: "Understand all modules and how they work together", icon: BookOpen, type: "doc" },
      { title: "First Course Walkthrough", desc: "Step-by-step guide to completing your first course", icon: Video, type: "video" },
    ],
  },
  {
    category: "Courses & Learning",
    items: [
      { title: "How to Access Courses", desc: "Find, enroll, and start your assigned courses", icon: BookOpen, type: "guide" },
      { title: "SCORM Course Player", desc: "Understanding the eLearning player controls", icon: FileText, type: "doc" },
      { title: "Learning Paths Explained", desc: "How learning paths work and tracking your progress", icon: GraduationCap, type: "guide" },
    ],
  },
  {
    category: "Compliance & Certificates",
    items: [
      { title: "Compliance Requirements", desc: "Understanding mandatory training and deadlines", icon: Shield, type: "doc" },
      { title: "Certificate Downloads", desc: "How to view and download your certificates", icon: FileText, type: "guide" },
    ],
  },
  {
    category: "Account & Security",
    items: [
      { title: "Profile Settings", desc: "Update your personal information and preferences", icon: HelpCircle, type: "guide" },
      { title: "Password & 2FA", desc: "Securing your account with strong passwords and 2FA", icon: Shield, type: "doc" },
    ],
  },
];

const typeStyle = {
  guide: { label: "Guide", color: "text-emerald-600", bg: "bg-emerald-50" },
  doc: { label: "Doc", color: "text-blue-600", bg: "bg-blue-50" },
  video: { label: "Video", color: "text-purple-600", bg: "bg-purple-50" },
};

const TrainingDocsPage = () => {
  const { user } = useAuth();
  const isLearner = user?.role === "learner";
  const variant = isLearner ? "learner" : user?.role === "tenant_admin" ? "tenant_admin" : "super_admin";

  return (
    <AdminLayout title="Training & Docs" variant={variant}>
      <div className="space-y-8 max-w-4xl" data-testid="training-docs-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
            Training & Documentation
          </h1>
          <p className="text-muted-foreground text-sm">Guides, tutorials, and documentation to help you get the most out of Odesie</p>
        </div>

        {resources.map((section, si) => (
          <motion.div
            key={si}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: si * 0.08 }}
          >
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
              {section.category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item, i) => {
                const style = typeStyle[item.type] || typeStyle.doc;
                const Icon = item.icon;
                return (
                  <Card key={i} className="hover:border-primary/20 hover:shadow-sm transition-all cursor-pointer group" data-testid={`resource-${si}-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`w-5 h-5 ${style.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            <h4 className="font-medium text-sm group-hover:text-primary transition-colors">{item.title}</h4>
                            <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{item.desc}</p>
                          <Badge className={`mt-2 ${style.bg} ${style.color} text-[9px]`}>{style.label}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        ))}

        {/* Additional Resources */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Need more help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">Can't find what you're looking for? Our support team is here to help.</p>
            <div className="flex gap-3">
              <Link to="/learner/support" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                <HelpCircle className="w-4 h-4" /> Contact Support
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TrainingDocsPage;
